export interface ColorTheme {
  name: string;
  gradient: string;
  headerGradient: string;
  avatarBg: string;
  borderColor: string;
  accentBar: string;
  linkColor: string;
  linkHoverColor: string;
  // Hex colors for wallet button compatibility
  primaryColorHex?: string;
  secondaryColorHex?: string;
  backgroundColorHex?: string;
  textColorHex?: string;
  borderColorHex?: string;
}

export const THEMES: Record<string, ColorTheme> = {
  sunset: {
    name: 'Sunset (Default)',
    gradient: 'from-[#f6d365] to-[#fda085]',
    headerGradient: 'from-[#667eea] to-[#764ba2]',
    avatarBg: 'from-[#87ceeb] via-[#98e4ff] to-[#d4f1f9]',
    borderColor: 'border-[#f4d03f]',
    accentBar: 'from-[#f6d365] to-[#fda085]',
    linkColor: 'text-[#667eea]',
    linkHoverColor: 'hover:text-[#764ba2]',
    primaryColorHex: '#f6d365',
    secondaryColorHex: '#fda085',
    backgroundColorHex: '#667eea',
    textColorHex: '#ffffff',
    borderColorHex: '#f4d03f'
  },
  ocean: {
    name: 'Ocean Blue',
    gradient: 'from-[#4facfe] to-[#00f2fe]',
    headerGradient: 'from-[#0093E9] to-[#80D0C7]',
    avatarBg: 'from-[#e0f7ff] via-[#b3e5fc] to-[#81d4fa]',
    borderColor: 'border-[#00bcd4]',
    accentBar: 'from-[#4facfe] to-[#00f2fe]',
    linkColor: 'text-[#0093E9]',
    linkHoverColor: 'hover:text-[#00bcd4]',
    primaryColorHex: '#4facfe',
    secondaryColorHex: '#00f2fe',
    backgroundColorHex: '#0093E9',
    textColorHex: '#ffffff',
    borderColorHex: '#00bcd4'
  },
  forest: {
    name: 'Forest Green',
    gradient: 'from-[#56ab2f] to-[#a8e063]',
    headerGradient: 'from-[#134e5e] to-[#71b280]',
    avatarBg: 'from-[#e8f5e9] via-[#c8e6c9] to-[#a5d6a7]',
    borderColor: 'border-[#66bb6a]',
    accentBar: 'from-[#56ab2f] to-[#a8e063]',
    linkColor: 'text-[#2e7d32]',
    linkHoverColor: 'hover:text-[#66bb6a]',
    primaryColorHex: '#56ab2f',
    secondaryColorHex: '#a8e063',
    backgroundColorHex: '#134e5e',
    textColorHex: '#ffffff',
    borderColorHex: '#66bb6a'
  },
  lavender: {
    name: 'Lavender Dream',
    gradient: 'from-[#d4a5f9] to-[#c084fc]',
    headerGradient: 'from-[#8b5cf6] to-[#a78bfa]',
    avatarBg: 'from-[#fae8ff] via-[#f3e8ff] to-[#e9d5ff]',
    borderColor: 'border-[#c084fc]',
    accentBar: 'from-[#d4a5f9] to-[#c084fc]',
    linkColor: 'text-[#8b5cf6]',
    linkHoverColor: 'hover:text-[#a78bfa]',
    primaryColorHex: '#d4a5f9',
    secondaryColorHex: '#c084fc',
    backgroundColorHex: '#8b5cf6',
    textColorHex: '#ffffff',
    borderColorHex: '#c084fc'
  },
  midnight: {
    name: 'Midnight',
    gradient: 'from-[#2c3e50] to-[#34495e]',
    headerGradient: 'from-[#1a1a2e] to-[#16213e]',
    avatarBg: 'from-[#ecf0f1] via-[#d5dbdb] to-[#bdc3c7]',
    borderColor: 'border-[#3498db]',
    accentBar: 'from-[#2c3e50] to-[#34495e]',
    linkColor: 'text-[#3498db]',
    linkHoverColor: 'hover:text-[#2980b9]',
    primaryColorHex: '#2c3e50',
    secondaryColorHex: '#34495e',
    backgroundColorHex: '#1a1a2e',
    textColorHex: '#ffffff',
    borderColorHex: '#3498db'
  },
  foundation: {
    name: 'Foundation Blue',
    gradient: 'from-[#D4AF37] to-[#eed189]',
    headerGradient: 'from-[#0d1b2a] to-[#1b263b]',
    avatarBg: 'from-[#4f97c4] via-[#b2ebf2] to-[#4f97c4]',
    borderColor: 'border-[#D4AF37]',
    accentBar: 'from-[#0a1a2e] to-[#0f2b47]',
    linkColor: 'text-[#257ec9]',
    linkHoverColor: 'hover:text-[#6bfefe]',
    primaryColorHex: '#D4AF37',
    secondaryColorHex: '#eed189',
    backgroundColorHex: '#0d1b2a',
    textColorHex: '#ffffff',
    borderColorHex: '#D4AF37'
  },
  cherry: {
    name: 'Cherry Blossom',
    gradient: 'from-[#ff9a9e] to-[#fecfef]',
    headerGradient: 'from-[#fa709a] to-[#fee140]',
    avatarBg: 'from-[#fff5f7] via-[#ffe4e9] to-[#ffc9d9]',
    borderColor: 'border-[#ff6b9d]',
    accentBar: 'from-[#ff9a9e] to-[#fecfef]',
    linkColor: 'text-[#e91e63]',
    linkHoverColor: 'hover:text-[#ff6b9d]',
    primaryColorHex: '#ff9a9e',
    secondaryColorHex: '#fecfef',
    backgroundColorHex: '#fa709a',
    textColorHex: '#ffffff',
    borderColorHex: '#ff6b9d'
  },
  fire: {
    name: 'Fire',
    gradient: 'from-[#f12711] to-[#f5af19]',
    headerGradient: 'from-[#c21500] to-[#ffc500]',
    avatarBg: 'from-[#fff3e0] via-[#ffe0b2] to-[#ffcc80]',
    borderColor: 'border-[#ff6f00]',
    accentBar: 'from-[#f12711] to-[#f5af19]',
    linkColor: 'text-[#d84315]',
    linkHoverColor: 'hover:text-[#ff6f00]',
    primaryColorHex: '#f12711',
    secondaryColorHex: '#f5af19',
    backgroundColorHex: '#c21500',
    textColorHex: '#ffffff',
    borderColorHex: '#ff6f00'
  },
  galaxy: {
    name: 'Galaxy',
    gradient: 'from-[#8e2de2] to-[#4a00e0]',
    headerGradient: 'from-[#2e1065] to-[#7c3aed]',
    avatarBg: 'from-[#f3e8ff] via-[#e9d5ff] to-[#d8b4fe]',
    borderColor: 'border-[#8b5cf6]',
    accentBar: 'from-[#8e2de2] to-[#4a00e0]',
    linkColor: 'text-[#7c3aed]',
    linkHoverColor: 'hover:text-[#8b5cf6]',
    primaryColorHex: '#8e2de2',
    secondaryColorHex: '#4a00e0',
    backgroundColorHex: '#2e1065',
    textColorHex: '#ffffff',
    borderColorHex: '#8b5cf6'
  },
  mint: {
    name: 'Fresh Mint',
    gradient: 'from-[#00b4db] to-[#0083b0]',
    headerGradient: 'from-[#00695c] to-[#00897b]',
    avatarBg: 'from-[#e0f2f1] via-[#b2dfdb] to-[#80cbc4]',
    borderColor: 'border-[#26a69a]',
    accentBar: 'from-[#00b4db] to-[#0083b0]',
    linkColor: 'text-[#00897b]',
    linkHoverColor: 'hover:text-[#26a69a]',
    primaryColorHex: '#00b4db',
    secondaryColorHex: '#0083b0',
    backgroundColorHex: '#00695c',
    textColorHex: '#ffffff',
    borderColorHex: '#26a69a'
  },
  royal: {
    name: 'Royal Purple',
    gradient: 'from-[#7f00ff] to-[#e100ff]',
    headerGradient: 'from-[#4a148c] to-[#7b1fa2]',
    avatarBg: 'from-[#f3e5f5] via-[#e1bee7] to-[#ce93d8]',
    borderColor: 'border-[#ab47bc]',
    accentBar: 'from-[#7f00ff] to-[#e100ff]',
    linkColor: 'text-[#7b1fa2]',
    linkHoverColor: 'hover:text-[#ab47bc]',
    primaryColorHex: '#7f00ff',
    secondaryColorHex: '#e100ff',
    backgroundColorHex: '#4a148c',
    textColorHex: '#ffffff',
    borderColorHex: '#ab47bc'
  }
};

export const DEFAULT_THEME = 'foundation';

export function getTheme(themeKey?: string | null): ColorTheme {
  if (themeKey && THEMES[themeKey]) {
    return THEMES[themeKey];
  }
  return THEMES[DEFAULT_THEME];
}
